"use client";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BottomNav } from "./ui";
import { useCart } from "@/store/cart";
import { useAppMode } from "@/hooks/useAppMode";
import { useBranch } from "@/store/branch";
import { MerchantUnavailable, isMerchantBlocked } from "./MerchantUnavailable";
import { InstallBanner } from "./InstallBanner";
import { storefrontName } from "@/lib/storefrontName";

// Slug now lives in the React Router basename (see src/lib/resolveSlug.ts +
// App.tsx). `useLocation().pathname` is already slug-relative, so prefix
// arithmetic is no longer needed.

const NAV_TABS = new Set([
  "/",
  "/home",
  "/history",
  "/order-history",
  "/profile",
  "/user-profile",
  "/cart",
]);

function tabFromPath(path: string): string {
  if (path === "/" || path === "/home") return "home";
  if (path === "/history" || path === "/order-history") return "history";
  if (path === "/profile" || path === "/user-profile") return "profile";
  if (path === "/cart") return "cart";
  return "home";
}

export function PhoneShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { cartCount } = useCart();
  const { isOrdering } = useAppMode();
  const { branchSlug, branchConfig, loading: branchLoading, error: branchError } = useBranch();
  const showBranchBanner =
    import.meta.env.DEV &&
    !branchLoading &&
    (!branchSlug || !branchConfig || !!branchError);

  const relativePath = location.pathname;
  // Every nav tab except Home is an ordering or account destination, so on a
  // menu-only link the bar would be a single lonely tab. The shortlist has its
  // own sticky bar instead (components/ShortlistBar.tsx).
  //
  // Gated on `isOrdering`, not `!isMenuOnly`: while config is resolving we
  // don't yet know which kind of link this is, and showing the nav then
  // yanking it away is the digital-menu flash we're trying to avoid. Route
  // guards deliberately do the opposite — see components/RequireOrdering.tsx.
  const showBottomNav = NAV_TABS.has(relativePath) && isOrdering;
  const tab = tabFromPath(relativePath);

  // Pre-empt the menu render for suspended / cancelled / expired merchants.
  // Backend order endpoints are also gated (subscription production-readiness
  // Phase 0.2); this is the UX layer so customers don't see an empty menu.
  // We deliberately allow `tracking/*` and `success` through so a customer
  // who placed an order before the merchant was suspended can still see it.
  const merchantBlocked = isMerchantBlocked(
    (branchConfig as { subscriptionStatus?: string | null } | null)
      ?.subscriptionStatus,
  );
  const isOrderingPath =
    relativePath === "/" ||
    relativePath === "/home" ||
    relativePath === "/cart" ||
    relativePath === "/checkout" ||
    relativePath === "/check-out" ||
    relativePath.startsWith("/item/") ||
    relativePath.startsWith("/item-detail/") ||
    relativePath === "/map-picker" ||
    relativePath === "/specify-location" ||
    relativePath === "/new-address" ||
    relativePath.startsWith("/new-address/") ||
    relativePath === "/addresses/new" ||
    relativePath === "/addresses";
  const renderUnavailable = merchantBlocked && isOrderingPath;

  const handleNav = (id: string) => {
    if (id === "home") navigate("/");
    else if (id === "history") navigate("/history");
    else if (id === "cart") navigate("/cart");
    else if (id === "profile") navigate("/profile");
  };

  return (
    <div className="app-root">
      <div className="phone-shell" data-screen-label={location.pathname}>
        <div className="phone-content">
          {showBranchBanner && (
            <div
              role="alert"
              style={{
                background: "#fff4e0",
                color: "#92400e",
                padding: "10px 14px",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.45,
                borderBottom: "1px solid #f3d99a",
              }}
            >
              {!branchSlug
                ? t("v2.shell.no_branch")
                : t("v2.shell.branch_load_failed")}
            </div>
          )}
          {renderUnavailable ? (
            <MerchantUnavailable
              restaurantName={branchConfig ? storefrontName(branchConfig) : null}
              logoUrl={
                (branchConfig as { logoUrl?: string | null } | null)?.logoUrl ??
                null
              }
            />
          ) : (
            children
          )}
        </div>
        {showBottomNav && !renderUnavailable && (
          <BottomNav active={tab} onChange={handleNav} cartCount={cartCount} />
        )}
      </div>
      {/*
        PWA install banner — branded with the tenant logo + name. Self-gates
        on platform + standalone + dismissal state via useInstallPrompt. We
        hide it when the merchant is blocked so we don't push installation
        of an unavailable storefront.
      */}
      {!renderUnavailable && <InstallBanner />}
    </div>
  );
}
